import { useEffect, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, ButtonTypeEnum, Dialog, DialogContent } from "@tenant/ui";
import defaultStyles from "./ChildAgesDialog.module.scss";

const COMPONENT_KEY = "ChildAgesDialog";

export type ChildAgesDialogProps = {
  open: boolean;
  childrenCount: number;
  infantsOnLapCount: number;
  infantsInSeatCount: number;
  onConfirm: (childAges: number[]) => void;
  onCancel: () => void;
};

/**
 * Collects ages (2–11) for child passengers before entering the package
 * upsell flow. Infants are shown as informational read-only rows labelled
 * "Under 2" — their age is determined by seating category (lap = 0, seat = 1)
 * in the hotel convention and cannot be meaningfully overridden by user input.
 */
export const ChildAgesDialog = ({
  open,
  childrenCount,
  infantsOnLapCount,
  infantsInSeatCount,
  onConfirm,
  onCancel,
}: ChildAgesDialogProps) => {
  const { t } = useI18nContext();
  const [, cn] = useModuleBEM(defaultStyles, COMPONENT_KEY);

  const totalInfants = infantsOnLapCount + infantsInSeatCount;

  const [ages, setAges] = useState<(number | null)[]>(() =>
    Array<null>(childrenCount).fill(null)
  );

  // Reset draft ages each time the dialog opens so stale selections don't persist.
  useEffect(() => {
    if (open) {
      setAges(Array<null>(childrenCount).fill(null));
    }
  }, [open, childrenCount]);

  const updateAge = (index: number, age: number) => {
    setAges((prev) => {
      const next = [...prev];
      next[index] = age;
      return next;
    });
  };

  const allAgesSelected = ages.every((a) => a !== null);

  const handleConfirm = () => {
    if (allAgesSelected) {
      onConfirm(ages as number[]);
    }
  };

  const placeholder = t(
    "core-hotels.search.guestPicker.child.selectPlaceHolder"
  );

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogContent
        title={t("flightReview.childAgesDialog.title")}
        description={t("flightReview.childAgesDialog.description")}
        bottomFixedContent={
          <div className={cn("actions")}>
            <Button
              buttonType={ButtonTypeEnum.Primary}
              onClick={handleConfirm}
              disabled={!allAgesSelected}
            >
              {t("flightReview.childAgesDialog.confirm")}
            </Button>
            <Button buttonType={ButtonTypeEnum.Tertiary} onClick={onCancel}>
              {t("flightReview.childAgesDialog.cancel")}
            </Button>
          </div>
        }
      >
        {Array.from({ length: childrenCount }, (_, i) => (
          <div key={i} className={cn("age-row")}>
            <label htmlFor={`child-age-${i}`} className={cn("age-label")}>
              {t("core-hotels.search.guestPicker.child.childIndexLabel", {
                index: i + 1,
              })}
            </label>
            <select
              id={`child-age-${i}`}
              className={cn("age-select")}
              value={ages[i] ?? ""}
              onChange={(e) => updateAge(i, Number(e.target.value))}
            >
              <option value="" disabled>
                {placeholder}
              </option>
              {Array.from({ length: 10 }, (_, j) => j + 2).map((age) => (
                <option key={age} value={age}>
                  {age}
                </option>
              ))}
            </select>
          </div>
        ))}
        {Array.from({ length: totalInfants }, (_, i) => (
          <div key={i} className={cn("age-row")}>
            <span className={cn("age-label")}>
              {t("core-hotels.search.guestPicker.child.childIndexLabel", {
                index: childrenCount + i + 1,
              })}
            </span>
            <span className={cn("infant-age")}>
              {t("flightReview.childAgesDialog.infantAge")}
            </span>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
